import { useState, useMemo, useEffect } from 'react'
import { useNavigate, useParams, useOutletContext, useLocation } from 'react-router-dom'
import { FeatureGate } from '@/components/shared/FeatureGate'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import ToolsLayout from '@/components/layout/ToolsLayout'
import { PageShell, PageHeader } from '@/components/shared/layout'
import {
  calculateSampleSize,
  calculateTestDuration,
  assessMDE,
  assessBaseline,
  formatNumber,
  formatDuration,
  formatReviewDate,
} from '@/lib/utils/abTestCalculations'
import {
  TEST_TYPE_CONFIGS,
  type TestType,
} from '@/lib/utils/abTestStatistics'
import { useAuth } from '@/lib/contexts/AuthContext'
import { type NavIconName } from '@/components/layout/navIcons'
import { useQuery } from '@tanstack/react-query'
import { getPagesBreakdown } from '@/lib/api/external-api'
import { HugeiconsIcon } from "@hugeicons/react"
import { Download04Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons"

// ============================================================================
// Types
// ============================================================================

const PAGE_TRAFFIC_PERIOD = 'day_PAST_30_D'
const PAGE_TRAFFIC_DAYS = 30

interface DashboardContext {
  propertyId?: string
  property?: any
  dashboardData?: any
  isLoading?: boolean
  isGuestModeOn?: boolean
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Goal cards: the sidebar's duotone glyphs (grey at rest, ink when selected,
 * like an active nav row) and one line per field, so the four cards stay the
 * same height. Paper → Experiments
 * → "Test planner · empty".
 */
const GOAL_CARDS: Record<TestType, { glyph: NavIconName; label: string; description: string; meta: string }> = {
  engagement: { glyph: 'clicks', label: 'Clicks', description: 'Buttons and links', meta: 'Fastest to decide' },
  conversion: { glyph: 'forms', label: 'Sign-ups', description: 'Forms and demos', meta: 'Most used' },
  revenue: { glyph: 'revenue', label: 'Revenue', description: 'Trials and payments', meta: 'Stripe · 14-day lag' },
  funnel: { glyph: 'funnels', label: 'Funnel', description: 'Steps completed', meta: 'Two or more pages' },
}

// ============================================================================
// Component
// ============================================================================

function TestPlannerInner() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()

  // Context detection
  const getBasePath = () => {
    if (params.workspaceId) return `/workspace/${params.workspaceId}`
    if (params.propertySlugId) return `/p/${params.propertySlugId}`
    return ''
  }
  const basePath = getBasePath()
  const hasContext = Boolean(basePath)

  const isInWorkspace = !!params.workspaceId || !!params.propertySlugId

  // Get dashboard data from outlet context (available in workspace/property routes)
  const outletContext = useOutletContext<DashboardContext | undefined>()
  const dashboardData = outletContext?.dashboardData

  // Derive daily visitors from analytics data passed through outlet context
  const analyticsDailyVisitors = useMemo(() => {
    if (!dashboardData) return null
    const data = dashboardData as any
    if (data?.summary?.visitors && data?.summary?.period_days) {
      return Math.round(data.summary.visitors / data.summary.period_days)
    }
    if (data?.summary?.totalVisitors && data?.summary?.periodDays) {
      return Math.round(data.summary.totalVisitors / data.summary.periodDays)
    }
    if (data?.metrics?.total_visitors && data?.metrics?.period_days) {
      return Math.round(data.metrics.total_visitors / data.metrics.period_days)
    }
    return null
  }, [dashboardData])

  // ---- Form State ----
  const [selectedGoal, setSelectedGoal] = useState<TestType | null>(null)
  const [baseline, setBaseline] = useState('5')
  const [mde, setMde] = useState('20')
  const [significance, setSignificance] = useState('95')
  const [power, setPower] = useState('80')
  const [dailyVisitors, setDailyVisitors] = useState('')
  const [numVariants, setNumVariants] = useState('2')
  const [dailyVisitorsAutoFilled, setDailyVisitorsAutoFilled] = useState(false)

  useEffect(() => {
    document.title = 'A/B test planner - Humblytics'
  }, [])

  // Traffic comes from the page being tested, not the whole site: a busy
  // homepage says nothing about how fast a pricing-page test will finish.
  const { token } = useAuth()
  const plannerPropertyId = outletContext?.propertyId
  const [testPage, setTestPage] = useState<string>('')
  const { data: pageTraffic } = useQuery({
    queryKey: ['planner-page-traffic', plannerPropertyId],
    queryFn: () => getPagesBreakdown(PAGE_TRAFFIC_PERIOD, plannerPropertyId!, token!, 50),
    enabled: Boolean(plannerPropertyId && token),
    staleTime: 5 * 60 * 1000,
  })
  const pageOptions = useMemo(
    () => (pageTraffic?.data ?? [])
      .filter(p => p.page && p.unique_visitors > 0)
      .map(p => ({ page: p.page, daily: Math.max(1, Math.round(p.unique_visitors / PAGE_TRAFFIC_DAYS)) })),
    [pageTraffic],
  )
  const selectTestPage = (page: string) => {
    setTestPage(page)
    const option = pageOptions.find(o => o.page === page)
    if (option) {
      setDailyVisitors(option.daily.toString())
      setDailyVisitorsAutoFilled(true)
    }
  }

  // Default to the busiest page once traffic loads; fall back to the
  // site-wide figure only when there is no page breakdown.
  useEffect(() => {
    if (dailyVisitors || !isInWorkspace) return
    if (pageOptions.length > 0) {
      selectTestPage(pageOptions[0].page)
    } else if (analyticsDailyVisitors) {
      setDailyVisitors(analyticsDailyVisitors.toString())
      setDailyVisitorsAutoFilled(true)
    }
  }, [analyticsDailyVisitors, isInWorkspace, pageOptions]) // eslint-disable-line react-hooks/exhaustive-deps

  // Goal selection auto-fills parameters
  const handleGoalSelect = (type: TestType) => {
    setSelectedGoal(type)
    const config = TEST_TYPE_CONFIGS[type]
    setMde(config.mdeRange[0].toString())
    setSignificance(config.significance.toString())
  }

  const selectedConfig = selectedGoal ? TEST_TYPE_CONFIGS[selectedGoal] : null

  // ---- Real-time Calculations ----
  const sampleSizeResult = useMemo(() => {
    const b = parseFloat(baseline)
    const m = parseFloat(mde)
    const s = parseFloat(significance)
    const p = parseFloat(power)
    if (!b || !m || !s || !p) return null
    if (b <= 0 || b >= 100) return null
    if (m <= 0 || m >= 100) return null
    if (s < 80 || s > 99.9) return null
    if (p < 50 || p > 99) return null
    try {
      return calculateSampleSize({ baseline: b, mde: m, significance: s, power: p })
    } catch {
      return null
    }
  }, [baseline, mde, significance, power])

  const durationResult = useMemo(() => {
    const b = parseFloat(baseline)
    const m = parseFloat(mde)
    const s = parseFloat(significance)
    const dv = parseFloat(dailyVisitors)
    const nv = parseInt(numVariants) || 2
    if (!b || !m || !s || !dv) return null
    if (b <= 0 || b >= 100) return null
    if (m <= 0 || m >= 100) return null
    if (dv <= 0) return null
    try {
      return calculateTestDuration({
        dailyVisitors: dv,
        baseline: b,
        mde: m,
        significance: s,
        variants: nv,
      })
    } catch {
      return null
    }
  }, [baseline, mde, significance, dailyVisitors, numVariants])

  // Parameter quality assessments
  const baselineAssessment = useMemo(() => {
    const b = parseFloat(baseline)
    if (!b || b <= 0 || b >= 100) return null
    return assessBaseline(b)
  }, [baseline])

  const mdeAssessment = useMemo(() => {
    const m = parseFloat(mde)
    if (!m || m <= 0 || m >= 100) return null
    return assessMDE(m)
  }, [mde])

  // ---- Actions ----
  const handleCreateTest = () => {
    const state: Record<string, any> = { fromPlanner: true }
    if (dailyVisitors) state.estimatedDailyVisitors = parseFloat(dailyVisitors)
    if (selectedGoal) state.testTypeContext = selectedGoal
    if (sampleSizeResult) state.sampleSizePerVariant = sampleSizeResult.sampleSizePerVariant
    if (durationResult) state.estimatedDuration = durationResult.days
    // The new-test page picker states the plan back ("spots a 20% lift ·
    // 95% sure it is real") and times each page with the same inputs.
    if (parseFloat(baseline)) state.baseline = parseFloat(baseline)
    if (parseFloat(mde)) state.mde = parseFloat(mde)
    if (parseFloat(significance)) state.significance = parseFloat(significance)
    navigate(`${basePath}/split-testing/new${location.search}`, { state })
  }

  const handleExportCSV = () => {
    if (!sampleSizeResult) return
    const rows = [
      ['Parameter', 'Value'],
      ['Test Goal', selectedGoal || 'Not selected'],
      ['Baseline Conversion Rate (%)', baseline],
      ['Minimum Detectable Effect (%)', mde],
      ['Statistical Significance (%)', significance],
      ['Statistical Power (%)', power],
      ['Number of Variants', numVariants],
      ['Required Sample Size per Variant', sampleSizeResult.sampleSizePerVariant.toString()],
      ['Total Sample Size', sampleSizeResult.totalSampleSize.toString()],
    ]
    if (durationResult) {
      rows.push(
        ['Daily Visitors', dailyVisitors],
        ['Estimated Duration (days)', durationResult.days.toString()],
        ['Review Date', formatReviewDate(durationResult.reviewDate)],
        ['Traffic Assessment', durationResult.trafficAssessment.severity],
      )
    }
    const csvContent = 'data:text/csv;charset=utf-8,' + rows.map(r => r.join(',')).join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', 'ab_test_planner_results.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success('Results exported to CSV!')
  }

  // ---- Render (Paper "A/B test planner": settings rows + curve on the left, the answer on the right) ----
  const Wrapper = hasContext
    ? ({ children }: { children: React.ReactNode }) => <>{children}</>
    : ToolsLayout

  const versions = parseInt(numVariants) || 2
  const dv = parseFloat(dailyVisitors) || 0
  const weeks = durationResult ? Math.ceil(durationResult.days / 7) : 0
  const tooLong = !!durationResult && durationResult.days > 180
  const selectedPage = pageOptions.find((o) => o.page === testPage)

  // Days needed across lifts, for the "bigger lifts show up sooner" curve.
  const curve = useMemo(() => {
    const b = parseFloat(baseline)
    const s = parseFloat(significance)
    if (!b || !s || !dv || b <= 0 || b >= 100) return null
    const lifts = Array.from({ length: 46 }, (_, i) => i + 5)
    const points = lifts.map((lift) => {
      try {
        return { lift, days: calculateTestDuration({ dailyVisitors: dv, baseline: b, mde: lift, significance: s, variants: versions }).days }
      } catch {
        return { lift, days: NaN }
      }
    }).filter((p) => Number.isFinite(p.days))
    return points.length > 1 ? points : null
  }, [baseline, significance, dv, versions])

  const [editMeasured, setEditMeasured] = useState(false)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const LIFTS = ['10', '20', '30', '50']

  return (
    <Wrapper>
      <PageShell className="flex flex-col gap-6">
        <PageHeader
          title="A/B test planner"
          description="Pick a page and what counts as a win. We'll tell you how long the test needs to run."
          className="mb-0"
        />

        <div className="well grid grid-cols-1 items-start gap-2 lg:grid-cols-[minmax(0,1fr)_392px]">
          <div className="flex min-w-0 flex-col gap-2">
            <section className="surface-raised flex flex-col overflow-clip [&>*+*]:border-t [&>*+*]:border-border">
              {isInWorkspace && pageOptions.length > 0 && (
                <PlannerRow title="Page to test" hint="Sorted by traffic">
                  <Select value={testPage} onValueChange={selectTestPage}>
                    <SelectTrigger className="w-full rounded-lg bg-background px-3.5 text-sm data-[size=default]:h-11">
                      <SelectValue placeholder="Pick a page">
                        {selectedPage && (
                          <span className="flex min-w-0 items-baseline gap-2">
                            <span className="truncate font-medium">{selectedPage.page === '/' ? 'Homepage' : selectedPage.page}</span>
                            <span className="truncate text-foreground/50">{selectedPage.daily} a day</span>
                          </span>
                        )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {pageOptions.map((o) => (
                        <SelectItem key={o.page} value={o.page} className="text-[13px]">
                          <span className="truncate">{o.page}</span>
                          <span className="ml-auto pl-3 text-foreground/50 tabular-nums">{o.daily} a day</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </PlannerRow>
              )}

              <PlannerRow title="What we measured" hint={dailyVisitorsAutoFilled ? `Last ${PAGE_TRAFFIC_DAYS} days on this page` : 'Enter your numbers'}>
                {editMeasured || !dailyVisitorsAutoFilled ? (
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Visitors a day</span>
                      <Input type="number" min="1" value={dailyVisitors} onChange={(e) => { setDailyVisitors(e.target.value); setDailyVisitorsAutoFilled(false) }} className="h-9 rounded-lg bg-background shadow-none" placeholder="644" />
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Conversion rate (%)</span>
                      <Input type="number" min="0.1" max="99" step="0.1" value={baseline} onChange={(e) => setBaseline(e.target.value)} className="h-9 rounded-lg bg-background shadow-none" placeholder="3.1" />
                    </label>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                    <div className="flex flex-col">
                      <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] tabular-nums">{formatNumber(dv)}</span>
                      <span className="text-xs leading-4 text-foreground/60">visitors a day</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[22px] font-semibold leading-7 tracking-[-0.02em] tabular-nums">{baseline}%</span>
                      <span className="text-xs leading-4 text-foreground/60">{selectedConfig ? `${GOAL_CARDS[selectedGoal!].label.toLowerCase()} rate` : 'conversion rate'}</span>
                    </div>
                    <span className="ml-auto flex items-center gap-3">
                      <span className="inline-flex h-6 items-center gap-[5px] rounded-md bg-muted px-2 text-xs font-medium leading-4">
                        <span className="size-1.5 rounded-full bg-live" />
                        From your data
                      </span>
                      <button type="button" onClick={() => setEditMeasured(true)} className="text-[13px] font-medium hover:text-foreground/70">
                        Edit
                      </button>
                    </span>
                  </div>
                )}
                {baselineAssessment && baselineAssessment.quality !== 'good' && (
                  <p className="mt-2 text-xs leading-4 text-foreground/60">{baselineAssessment.hint}</p>
                )}
              </PlannerRow>

              <PlannerRow title="What counts as a win?" hint={selectedConfig ? GOAL_CARDS[selectedGoal!].meta : 'Sets the confidence and lift to expect'}>
                <div role="radiogroup" aria-label="Test goal" className="flex rounded-lg bg-muted p-[3px]">
                  {(Object.keys(TEST_TYPE_CONFIGS) as TestType[]).map((type) => (
                    <button key={type} type="button" role="radio" aria-checked={selectedGoal === type} onClick={() => handleGoalSelect(type)} className={plannerSegment(selectedGoal === type)}>
                      {GOAL_CARDS[type].label}
                    </button>
                  ))}
                </div>
              </PlannerRow>

              <PlannerRow title="Smallest lift worth catching" hint="Smaller lifts need far more traffic">
                <div role="radiogroup" aria-label="Smallest lift" className="flex rounded-lg bg-muted p-[3px]">
                  {LIFTS.map((v) => (
                    <button key={v} type="button" role="radio" aria-checked={mde === v} onClick={() => setMde(v)} className={plannerSegment(mde === v)}>
                      {v}%
                    </button>
                  ))}
                </div>
                {!LIFTS.includes(mde) && mde && <p className="mt-2 text-xs leading-4 text-foreground/60">Custom: {mde}% (set under Advanced)</p>}
                {mdeAssessment && mdeAssessment.quality !== 'good' && <p className="mt-2 text-xs leading-4 text-foreground/60">{mdeAssessment.hint}</p>}
              </PlannerRow>

              <div className="bg-surface">
                <button
                  type="button"
                  onClick={() => setAdvancedOpen((v) => !v)}
                  aria-expanded={advancedOpen}
                  className="flex w-full items-center gap-2 px-6 py-3.5 text-left text-[13px] leading-4"
                >
                  <HugeiconsIcon icon={ArrowRight01Icon} size={13} className={cn('text-foreground/50 transition-transform', advancedOpen && 'rotate-90')} />
                  <span className="font-medium">Advanced</span>
                  <span className="truncate text-foreground/60">{significance}% confidence · {power}% power · {versions} versions ({versions === 2 ? 'A/B' : 'A/B/n'})</span>
                </button>
                {advancedOpen && (
                  <div className="grid grid-cols-2 gap-3 px-6 pb-5 sm:grid-cols-4">
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Confidence</span>
                      <Select value={significance} onValueChange={setSignificance}>
                        <SelectTrigger className="w-full rounded-lg bg-background data-[size=default]:h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['90', '95', '99'].map((v) => <SelectItem key={v} value={v}>{v}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Power</span>
                      <Select value={power} onValueChange={setPower}>
                        <SelectTrigger className="w-full rounded-lg bg-background data-[size=default]:h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['70', '80', '90'].map((v) => <SelectItem key={v} value={v}>{v}%</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Versions</span>
                      <Select value={numVariants} onValueChange={setNumVariants}>
                        <SelectTrigger className="w-full rounded-lg bg-background data-[size=default]:h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {['2', '3', '4'].map((v) => <SelectItem key={v} value={v}>{v} versions</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-xs font-medium leading-4">Custom lift (%)</span>
                      <Input type="number" min="1" max="99" value={mde} onChange={(e) => setMde(e.target.value)} className="h-9 rounded-lg bg-background shadow-none" />
                    </label>
                  </div>
                )}
              </div>
            </section>

            {curve && (
              <section className="surface-raised flex flex-col gap-3 px-6 py-5">
                <div className="flex flex-col gap-0.5">
                  <h2 className="text-sm font-medium leading-[18px]">Bigger lifts show up sooner</h2>
                  <p className="text-xs leading-4 text-foreground/60">Days needed at {formatNumber(dv)} visitors a day, rounded to full weeks</p>
                </div>
                <LiftCurve points={curve} current={parseFloat(mde)} />
              </section>
            )}
          </div>

          <div className="flex flex-col gap-2 lg:sticky lg:top-4">
            <section className="surface-raised flex flex-col gap-5 px-6 py-5">
              <div className="flex flex-col gap-1.5">
                <span className="text-[13px] leading-4 text-foreground/60">Run it for</span>
                <span className="text-[28px] font-semibold leading-[34px] tracking-[-0.02em] sm:text-[34px] sm:leading-10">
                  {durationResult ? (tooLong ? 'Longer than 6 months' : `About ${formatDuration(durationResult.days)}`) : 'Add your traffic'}
                </span>
                <p className="text-[13px] leading-5 text-foreground/60">
                  {durationResult
                    ? tooLong
                      ? `At ${formatNumber(dv)} visitors a day this page can't reach a result in time. Try a busier page or a bigger lift.`
                      : `${durationResult.days} days at ${formatNumber(dv)} visitors a day. Full weeks, so weekdays and weekends count evenly.`
                    : 'Pick a page or enter visitors a day to see how long the test takes.'}
                </p>
              </div>
              {durationResult && !tooLong && (
                <div className="flex flex-col gap-2">
                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(weeks, 26) }, (_, i) => (
                      <span key={i} className="h-1.5 flex-1 rounded-full bg-primary" />
                    ))}
                  </div>
                  <div className="flex justify-between text-xs leading-4 text-foreground/50">
                    <span>Today</span>
                    <span>{durationResult.reviewDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                  </div>
                </div>
              )}
              {sampleSizeResult && (
                <dl className="flex flex-col border-t border-border pt-4 text-[13px] leading-4 [&>div+div]:pt-3">
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground/60">Visitors needed per version</dt>
                    <dd className="font-semibold tabular-nums">{formatNumber(sampleSizeResult.sampleSizePerVariant)}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-foreground/60">In total, across {versions === 2 ? 'A and B' : `${versions} versions`}</dt>
                    <dd className="font-semibold tabular-nums">{formatNumber(sampleSizeResult.sampleSizePerVariant * versions)}</dd>
                  </div>
                </dl>
              )}
              {/* Same duration model as the headline, so the numbers never disagree. */}
              {durationResult && !tooLong && durationResult.trafficAssessment.severity !== 'good' && (
                <p className="rounded-lg bg-warning-tint px-3 py-2.5 text-xs leading-[18px] text-foreground shadow-[0_0_0_1px_#FEDF89] dark:shadow-[0_0_0_1px_rgb(247_144_9/0.35)]">
                  Slow for this page. A bigger lift, or a page with more visitors, gets you an answer sooner.
                </p>
              )}
            </section>
            {hasContext && (
              <Button onClick={handleCreateTest} disabled={!sampleSizeResult} className="h-11 rounded-lg text-sm">
                Create test with these settings
              </Button>
            )}
            <button
              type="button"
              onClick={handleExportCSV}
              disabled={!sampleSizeResult}
              className="flex items-center justify-center gap-1.5 py-1 text-[13px] text-foreground/60 transition-colors hover:text-foreground disabled:opacity-40"
            >
              <HugeiconsIcon icon={Download04Icon} size={13} />
              Export as CSV
            </button>
          </div>
        </div>
      </PageShell>
    </Wrapper>
  )
}


const plannerSegment = (on: boolean) =>
  cn(
    'h-8 flex-1 rounded-md px-2 text-[13px] leading-4 transition-colors',
    on
      ? 'bg-card font-medium text-foreground shadow-[0_0_0_1px_rgb(9_9_11/0.05),0_1px_2px_rgb(0_0_0/0.06)] dark:bg-white/10 dark:shadow-none'
      : 'text-foreground/60 hover:text-foreground',
  )

function PlannerRow({ title, hint, children }: { title: string; hint: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3 px-6 py-5 md:flex-row md:items-center md:gap-8">
      <div className="flex shrink-0 flex-col gap-0.5 md:w-[220px]">
        <span className="text-sm font-medium leading-[18px]">{title}</span>
        <span className="text-xs leading-4 text-foreground/60">{hint}</span>
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  )
}

/** Days needed per lift, drawn as one soft area with the chosen lift marked. */
function LiftCurve({ points, current }: { points: { lift: number; days: number }[]; current: number }) {
  const W = 640
  const H = 150
  const pad = { l: 40, r: 26, t: 8, b: 20 }
  const mark0 = points.reduce((best, p) => (Math.abs(p.lift - current) < Math.abs(best.lift - current) ? p : best), points[0])
  // Scale to half the chosen lift, not the tiny-lift extreme, so the useful part of the curve has room.
  const ref = points.reduce((best, p) => (Math.abs(p.lift - Math.max(5, current / 2)) < Math.abs(best.lift - Math.max(5, current / 2)) ? p : best), points[0])
  const top = Math.max(28, Math.ceil(Math.max(ref.days, mark0.days * 1.5) / 7) * 7)
  const dayLabel = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k d` : `${n} d`)
  const x = (lift: number) => pad.l + ((lift - 5) / 45) * (W - pad.l - pad.r)
  const y = (days: number) => pad.t + (1 - Math.min(days, top) / top) * (H - pad.t - pad.b)
  const line = points.map((p, i) => `${i ? 'L' : 'M'}${x(p.lift).toFixed(1)},${y(p.days).toFixed(1)}`).join('')
  const area = `${line}L${x(points[points.length - 1].lift)},${H - pad.b}L${x(points[0].lift)},${H - pad.b}Z`
  const mark = mark0
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full overflow-hidden" role="img" aria-label={`At a ${mark.lift}% lift the test needs about ${mark.days} days`}>
      <defs>
        <linearGradient id="lift-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.22" />
          <stop offset="100%" stopColor="var(--primary)" stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((f) => (
        <g key={f}>
          <line x1={pad.l} x2={W - pad.r} y1={y(top * f)} y2={y(top * f)} stroke="currentColor" strokeOpacity="0.07" strokeDasharray="3 3" />
          <text x={pad.l - 6} y={y(top * f) + 3} textAnchor="end" className="fill-foreground/50 text-[10px]">{dayLabel(Math.round(top * f))}</text>
        </g>
      ))}
      {[10, 20, 30, 40, 50].map((l) => (
        <text key={l} x={x(l)} y={H - 4} textAnchor="middle" className={cn('text-[10px]', l === mark.lift ? 'fill-foreground font-semibold' : 'fill-foreground/50')}>{l}%</text>
      ))}
      <clipPath id="lift-plot"><rect x={pad.l} y={pad.t} width={W - pad.l - pad.r} height={H - pad.t - pad.b} /></clipPath>
      <path d={area} fill="url(#lift-area)" clipPath="url(#lift-plot)" />
      <path d={line} fill="none" stroke="var(--primary)" strokeWidth="1.5" clipPath="url(#lift-plot)" />
      <line x1={x(mark.lift)} x2={x(mark.lift)} y1={y(mark.days)} y2={H - pad.b} stroke="currentColor" strokeOpacity="0.15" />
      <circle cx={x(mark.lift)} cy={y(mark.days)} r="4" fill="var(--card)" stroke="var(--primary)" strokeWidth="1.5" />
      <g transform={`translate(${Math.min(Math.max(x(mark.lift) + 8, pad.l), W - 110)},${Math.max(y(mark.days) - 24, pad.t)})`}>
        <rect width="100" height="20" rx="5" fill="var(--foreground)" />
        <text x="50" y="13.5" textAnchor="middle" className="fill-background text-[10px] font-medium">{mark.lift}% lift · {mark.days} days</text>
      </g>
    </svg>
  )
}

export default function TestPlanner() {
  return (
    <FeatureGate feature="splitTesting">
      <TestPlannerInner />
    </FeatureGate>
  )
}
